import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import Select from './Select'
import SessionTimer from './SessionTimer'
import MiniStopwatch from './MiniStopwatch'
import {
  X,
  Save,
  Clock,
  Heart,
  Activity,
  Thermometer,
  Gauge,
  Weight,
  Ruler,
  FileText,
  Bell,
  CheckCircle2,
} from 'lucide-react'
import { formatDate } from '../utils/helpers'
import { saveToLocalStorage, loadFromLocalStorage, clearLocalStorage, AUTOSAVE_INTERVAL } from '../services/autosave'
import { saveDraft, loadDraft } from '../services/draftSaving'
import DraftManager from './DraftManager'
import { FolderOpen } from 'lucide-react'

const LiveSessionRecorder = ({ clientId: initialClientId, onClose, onSave }) => {
  const { t } = useTranslation()
  const [selectedClientId, setSelectedClientId] = useState(initialClientId || '')
  
  const VITAL_SIGN_INTERVALS = [
    { value: '5', label: t('sessions.vitalIntervals.5') },
    { value: '10', label: t('sessions.vitalIntervals.10') },
    { value: '15', label: t('sessions.vitalIntervals.15') },
    { value: '30', label: t('sessions.vitalIntervals.30') },
    { value: '60', label: t('sessions.vitalIntervals.60') },
    { value: '0', label: t('sessions.vitalIntervals.0') },
  ]
  const [clients, setClients] = useState([])
  const [loadingClients, setLoadingClients] = useState(false)
  const organizationId = useOrganizationId()
  const [sessionDuration, setSessionDuration] = useState(0)
  const [vitalSignsTimer, setVitalSignsTimer] = useState(0)
  const [vitalSignsInterval, setVitalSignsInterval] = useState('15')
  const [nextVitalCheck, setNextVitalCheck] = useState(null)
  const [showVitalReminder, setShowVitalReminder] = useState(false)
  const [vitalSigns, setVitalSigns] = useState([])
  const [quickNotes, setQuickNotes] = useState([])
  const [currentNote, setCurrentNote] = useState('')
  const [sessionData, setSessionData] = useState({
    focusArea: '',
    outcomeSummary: '',
  })
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle') // idle, saving, saved, error
  const sessionIdRef = useRef(`session_${Date.now()}`)
  const autoSaveIntervalRef = useRef(null)
  const intervalRef = useRef(null)
  const reminderRef = useRef(null)
  const hasUserInputRef = useRef(false)
  const draftSaveTimeoutRef = useRef(null)
  const [showDraftManager, setShowDraftManager] = useState(false)
  const [dataRestored, setDataRestored] = useState(false)
  const [userId, setUserId] = useState(null)

  useEffect(() => {
    // Get user ID
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) setUserId(user.id)
    }
    getUser()

    if (organizationId && !initialClientId) {
      fetchClients()
    }
    
    // Load autosaved data (localStorage + server draft)
    const loadAutosaved = async () => {
      // Try localStorage first
      const autosaved = loadFromLocalStorage(sessionIdRef.current)
      if (autosaved) {
        // Auto-restore for mobile/page reload scenarios
        setSessionDuration(autosaved.sessionDuration || 0)
        setVitalSigns(autosaved.vitalSigns || [])
        setQuickNotes(autosaved.quickNotes || [])
        setSessionData(autosaved.sessionData || { focusArea: '', outcomeSummary: '' })
        setSelectedClientId(autosaved.selectedClientId || '')
        setVitalSignsInterval(autosaved.vitalSignsInterval || '15')
        setDataRestored(true)
        setTimeout(() => setDataRestored(false), 3000)
        return
      }

      // Try server draft if no localStorage
      if (organizationId && userId) {
        const serverDraft = await loadDraft(organizationId, userId, 'session', null)
        if (serverDraft && serverDraft.draft_data) {
          const draftData = serverDraft.draft_data
          // Auto-restore from server draft
          setSessionDuration(draftData.sessionDuration || 0)
          setVitalSigns(draftData.vitalSigns || [])
          setQuickNotes(draftData.quickNotes || [])
          setSessionData(draftData.sessionData || { focusArea: '', outcomeSummary: '' })
          setSelectedClientId(draftData.selectedClientId || '')
          setVitalSignsInterval(draftData.vitalSignsInterval || '15')
          setDataRestored(true)
          setTimeout(() => setDataRestored(false), 3000)
        }
      }
    }

    if (organizationId && userId) {
      loadAutosaved()
    }
  }, [organizationId, initialClientId, userId])

  // Autosave effect - saves on input changes (localStorage + server)
  // NOTE: sessionDuration is NOT in dependencies - timer updates should NOT trigger saves
  useEffect(() => {
    const autoSave = async () => {
      // Only save if user has made input
      if (!hasUserInputRef.current) return

      const dataToSave = {
        sessionDuration, // Include current duration but don't trigger on timer updates
        vitalSigns,
        quickNotes,
        sessionData,
        selectedClientId: selectedClientId || initialClientId,
        vitalSignsInterval,
        clientId: selectedClientId || initialClientId,
      }

      setAutoSaveStatus('saving')
      
      // Save to localStorage
      const saved = saveToLocalStorage(sessionIdRef.current, dataToSave)
      
      // Also save to server draft if we have organizationId and userId
      if (organizationId && userId) {
        try {
          await saveDraft(organizationId, userId, 'session', null, dataToSave)
        } catch (error) {
          console.error('Error saving draft to server:', error)
          // Don't fail autosave if server save fails
        }
      }
      
      if (saved) {
        setAutoSaveStatus('saved')
        setLastSaved(new Date())
        // Reset status after 2 seconds
        setTimeout(() => {
          setAutoSaveStatus('idle')
        }, 2000)
      } else {
        setAutoSaveStatus('error')
      }
    }

    // Clear any pending timeout
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current)
    }

    // Debounce autosave - save 1 second after last input change
    draftSaveTimeoutRef.current = setTimeout(() => {
      autoSave()
    }, 1000)

    // Also autosave every 30 seconds (only if user has made input)
    autoSaveIntervalRef.current = setInterval(autoSave, AUTOSAVE_INTERVAL)

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
      }
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current)
      }
    }
  }, [vitalSigns, quickNotes, sessionData, selectedClientId, vitalSignsInterval, initialClientId, organizationId, userId]) // Removed sessionDuration from dependencies

  const fetchClients = async () => {
    if (!organizationId) return
    try {
      setLoadingClients(true)
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name')
        .eq('organization_id', organizationId)
        .order('first_name')
      if (error) throw error
      setClients(data || [])
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setLoadingClients(false)
    }
  }

  useEffect(() => {
    if (vitalSignsInterval !== '0' && sessionDuration > 0) {
      const intervalMinutes = parseInt(vitalSignsInterval)
      if (intervalMinutes > 0) {
        const nextCheck = sessionDuration + intervalMinutes * 60
        setNextVitalCheck(nextCheck)
      }
    }
  }, [vitalSignsInterval])

  useEffect(() => {
    if (nextVitalCheck && sessionDuration >= nextVitalCheck && !showVitalReminder) {
      setShowVitalReminder(true)
      // Auto-hide reminder after 30 seconds
      reminderRef.current = setTimeout(() => {
        setShowVitalReminder(false)
      }, 30000)
    }

    return () => {
      if (reminderRef.current) {
        clearTimeout(reminderRef.current)
      }
    }
  }, [sessionDuration, nextVitalCheck, showVitalReminder])

  const handleAddVitalSigns = () => {
    hasUserInputRef.current = true
    const newVital = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      bloodPressure: '',
      heartRate: '',
      respiratoryRate: '',
      temperature: '',
      oxygenSaturation: '',
      weight: '',
      height: '',
      notes: '',
    }
    setVitalSigns([...vitalSigns, newVital])
    setShowVitalReminder(false)
    if (vitalSignsInterval !== '0') {
      const intervalMinutes = parseInt(vitalSignsInterval)
      if (intervalMinutes > 0) {
        setNextVitalCheck(sessionDuration + intervalMinutes * 60)
      }
    }
  }

  const handleUpdateVitalSign = (id, field, value) => {
    hasUserInputRef.current = true
    setVitalSigns(
      vitalSigns.map((v) => (v.id === id ? { ...v, [field]: value } : v))
    )
    // Trigger immediate autosave on input change
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current)
    }
  }

  const handleRemoveVitalSign = (id) => {
    hasUserInputRef.current = true
    setVitalSigns(vitalSigns.filter((v) => v.id !== id))
  }

  const handleAddQuickNote = () => {
    if (!currentNote.trim()) return
    hasUserInputRef.current = true
    const note = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      text: currentNote.trim(),
      sessionTime: formatTime(sessionDuration),
    }
    setQuickNotes([...quickNotes, note])
    setCurrentNote('')
    // Trigger immediate autosave on input change
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current)
    }
  }

  // Handler to load a draft from DraftManager
  const handleLoadDraft = (draft) => {
    if (draft.sessionDuration !== undefined) {
      setSessionDuration(draft.sessionDuration || 0)
    }
    if (draft.vitalSigns) {
      setVitalSigns(draft.vitalSigns || [])
    }
    if (draft.quickNotes) {
      setQuickNotes(draft.quickNotes || [])
    }
    if (draft.sessionData) {
      setSessionData(draft.sessionData || { focusArea: '', outcomeSummary: '' })
    }
    if (draft.selectedClientId) {
      setSelectedClientId(draft.selectedClientId)
    }
    if (draft.vitalSignsInterval) {
      setVitalSignsInterval(draft.vitalSignsInterval)
    }
    setDataRestored(true)
    setTimeout(() => setDataRestored(false), 3000)
    setShowDraftManager(false)
    hasUserInputRef.current = true
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const handleSaveSession = async () => {
    const finalClientId = selectedClientId || initialClientId
    if (!finalClientId || !organizationId) {
      alert(t('sessions.live.pleaseSelectClient'))
      return
    }

    try {
      setSaving(true)
      const { data: { user } } = await supabase.auth.getUser()

      // Save session
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .insert({
          organization_id: organizationId,
          client_id: finalClientId,
          session_date: new Date().toISOString(),
          duration_minutes: Math.floor(sessionDuration / 60),
          focus_area: sessionData.focusArea || null,
          outcome_summary: sessionData.outcomeSummary || 'Session recorded with live monitoring.',
        })
        .select()
        .single()

      if (sessionError) throw sessionError

      // Save vital signs as examinations if any were recorded
      if (vitalSigns.length > 0) {
        for (const vital of vitalSigns) {
          const vitalData = {
            bloodPressure: vital.bloodPressure || null,
            heartRate: vital.heartRate || null,
            respiratoryRate: vital.respiratoryRate || null,
            temperature: vital.temperature || null,
            oxygenSaturation: vital.oxygenSaturation || null,
            weight: vital.weight || null,
            height: vital.height || null,
          }

          // Only create examination if at least one vital sign is recorded
          if (Object.values(vitalData).some((v) => v)) {
            // Get latest medical history for this client
            const { data: latestHistory } = await supabase
              .from('medical_histories')
              .select('id')
              .eq('client_id', finalClientId)
              .eq('organization_id', organizationId)
              .order('created_at', { ascending: false })
              .limit(1)
              .single()

            await supabase.from('examinations').insert({
              organization_id: organizationId,
              client_id: finalClientId,
              medical_history_id: latestHistory?.id || null,
              vital_signs: vitalData,
              custom_questions: [],
              created_at: vital.timestamp,
            })
          }
        }
      }

      // Save quick notes as progress notes
      if (quickNotes.length > 0 && session) {
        for (const note of quickNotes) {
          await supabase.from('progress_notes').insert({
            organization_id: organizationId,
            session_id: session.id,
            note: `[${note.sessionTime}] ${note.text}`,
            created_at: note.timestamp,
          })
        }
      }

      // Clear autosave and draft after successful save
      clearLocalStorage(sessionIdRef.current)
      
      // Delete server draft if it exists
      if (organizationId && userId) {
        try {
          const existingDraft = await loadDraft(organizationId, userId, 'session', null)
          if (existingDraft) {
            await supabase
              .from('form_drafts')
              .delete()
              .eq('organization_id', organizationId)
              .eq('user_id', userId)
              .eq('form_type', 'session')
              .is('form_id', null)
          }
        } catch (error) {
          console.error('Error deleting draft after save:', error)
          // Don't fail save if draft deletion fails
        }
      }
      
      if (onSave) {
        onSave(session)
      }
      onClose()
    } catch (error) {
      console.error('Error saving session:', error)
      alert(error.message || t('sessions.live.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{t('sessions.live.title')}</h2>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('sessions.live.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Autosave Status */}
            <div className="flex items-center gap-2 text-xs">
              {autoSaveStatus === 'saving' && (
                <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Clock size={14} className="animate-spin" />
                  {t('sessions.live.saving')}
                </span>
              )}
              {autoSaveStatus === 'saved' && (
                <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle2 size={14} />
                  {t('sessions.live.saved')}
                </span>
              )}
              {autoSaveStatus === 'error' && (
                <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                  <X size={14} />
                  {t('sessions.live.saveFailed')}
                </span>
              )}
              {lastSaved && autoSaveStatus === 'idle' && (
                <span className="text-gray-400 dark:text-gray-500">
                  {t('sessions.live.lastSaved')}: {new Date(lastSaved).toLocaleTimeString()}
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={() => setShowDraftManager(true)}
              className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1"
            >
              <FolderOpen size={14} className="sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{t('sessions.live.viewDrafts')}</span>
            </Button>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {/* Client Selection (if not provided) */}
          {!initialClientId && (
            <Card variant="soft" className="p-4 sm:p-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('sessions.live.selectClient')} <span className="text-red-500">*</span>
              </label>
              {loadingClients ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('sessions.live.loadingClients')}</p>
              ) : (
                <Select
                  options={clients.map((c) => ({
                    value: c.id,
                    label: `${c.first_name} ${c.last_name}`,
                  }))}
                  value={selectedClientId}
                  onChange={(e) => {
                    hasUserInputRef.current = true
                    setSelectedClientId(e.target.value)
                  }}
                  required
                />
              )}
            </Card>
          )}

          {/* Main Timer */}
          <Card variant="soft" className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock size={18} className="sm:w-5 sm:h-5" />
                {t('sessions.live.sessionDuration')}
              </h3>
            </div>
            <SessionTimer
              onDurationChange={(duration) => {
                hasUserInputRef.current = true
                setSessionDuration(duration)
              }}
              initialDuration={0}
            />
          </Card>

          {/* Vital Signs Reminder */}
          <AnimatePresence>
            {showVitalReminder && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="p-3 sm:p-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Bell className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6" size={20} />
                  <div>
                    <p className="font-semibold text-yellow-900 dark:text-yellow-200 text-sm sm:text-base">{t('sessions.live.timeToCheckVitals')}</p>
                    <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-300">{t('sessions.live.sessionTime')}: {formatTime(sessionDuration)}</p>
                  </div>
                </div>
                <Button onClick={handleAddVitalSigns} className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 w-full sm:w-auto">
                  {t('sessions.live.recordVitals')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Vital Signs Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <MiniStopwatch
              label={t('sessions.live.vitalSignsTimer')}
              onTimeChange={setVitalSignsTimer}
            />
            <div className="p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('sessions.live.checkInterval')}
              </label>
              <Select
                options={VITAL_SIGN_INTERVALS}
                value={vitalSignsInterval}
                onChange={(e) => {
                  hasUserInputRef.current = true
                  setVitalSignsInterval(e.target.value)
                  // Trigger immediate autosave on input change
                  if (draftSaveTimeoutRef.current) {
                    clearTimeout(draftSaveTimeoutRef.current)
                  }
                }}
              />
              {nextVitalCheck && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('sessions.live.nextCheckAt')}: {formatTime(nextVitalCheck)}
                </p>
              )}
            </div>
          </div>

          {/* Recorded Vital Signs */}
          <Card variant="soft" className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Activity size={18} className="sm:w-5 sm:h-5" />
                {t('sessions.live.recordedVitalSigns')} ({vitalSigns.length})
              </h3>
              <Button onClick={handleAddVitalSigns} className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 w-full sm:w-auto">
                {t('sessions.live.addVitals')}
              </Button>
            </div>
            <div className="space-y-4">
              {vitalSigns.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('sessions.live.noVitalsRecorded')}
                </p>
              ) : (
                vitalSigns.map((vital, index) => (
                  <Card key={vital.id} variant="soft" className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {t('sessions.live.reading')} #{index + 1} - {formatTime(Math.floor((new Date(vital.timestamp) - new Date(vitalSigns[0]?.timestamp || vital.timestamp)) / 1000))}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(vital.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveVitalSign(vital.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                      <Input
                        label={t('sessions.live.bp')}
                        value={vital.bloodPressure}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'bloodPressure', e.target.value)}
                        placeholder="120/80"
                      />
                      <Input
                        label={t('sessions.live.heartRate')}
                        type="number"
                        value={vital.heartRate}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'heartRate', e.target.value)}
                        placeholder="72"
                      />
                      <Input
                        label={t('sessions.live.respRate')}
                        type="number"
                        value={vital.respiratoryRate}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'respiratoryRate', e.target.value)}
                        placeholder="16"
                      />
                      <Input
                        label={t('sessions.live.temp')}
                        type="number"
                        step="0.1"
                        value={vital.temperature}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'temperature', e.target.value)}
                        placeholder="36.5"
                      />
                      <Input
                        label={t('sessions.live.spo2')}
                        type="number"
                        value={vital.oxygenSaturation}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'oxygenSaturation', e.target.value)}
                        placeholder="98"
                      />
                      <Input
                        label={t('sessions.live.weight')}
                        type="number"
                        step="0.1"
                        value={vital.weight}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'weight', e.target.value)}
                        placeholder="70"
                      />
                      <Input
                        label={t('sessions.live.height')}
                        type="number"
                        value={vital.height}
                        onChange={(e) => handleUpdateVitalSign(vital.id, 'height', e.target.value)}
                        placeholder="170"
                      />
                    </div>
                    <Textarea
                      label={t('sessions.live.notes')}
                      value={vital.notes}
                      onChange={(e) => handleUpdateVitalSign(vital.id, 'notes', e.target.value)}
                      placeholder={t('sessions.live.notesPlaceholder')}
                      rows={2}
                      className="mt-3"
                    />
                  </Card>
                ))
              )}
            </div>
          </Card>

          {/* Quick Notes */}
          <Card variant="soft" className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <FileText size={18} className="sm:w-5 sm:h-5" />
              {t('sessions.live.quickNotes')}
            </h3>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <Textarea
                  value={currentNote}
                  onChange={(e) => {
                    hasUserInputRef.current = true
                    setCurrentNote(e.target.value)
                    // Trigger immediate autosave on input change
                    if (draftSaveTimeoutRef.current) {
                      clearTimeout(draftSaveTimeoutRef.current)
                    }
                  }}
                  placeholder={t('sessions.live.addQuickNote')}
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleAddQuickNote()
                    }
                  }}
                  className="flex-1"
                />
                <Button onClick={handleAddQuickNote} disabled={!currentNote.trim()} className="text-xs sm:text-sm px-3 sm:px-4 py-2 w-full sm:w-auto">
                  {t('common.buttons.add')}
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Press Ctrl+Enter to add note</p>
              {quickNotes.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {quickNotes.map((note) => (
                    <div
                      key={note.id}
                      className="p-3 rounded-lg bg-white/60 border border-gray-100"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-primary-600">{note.sessionTime}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(note.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{note.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Session Summary */}
          <Card variant="soft" className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">{t('sessions.live.sessionSummary')}</h3>
            <div className="space-y-4">
              <Input
                label={t('sessions.live.focusArea')}
                value={sessionData.focusArea}
                onChange={(e) => {
                  hasUserInputRef.current = true
                  setSessionData({ ...sessionData, focusArea: e.target.value })
                  // Trigger immediate autosave on input change
                  if (draftSaveTimeoutRef.current) {
                    clearTimeout(draftSaveTimeoutRef.current)
                  }
                }}
                placeholder={t('sessions.live.focusAreaPlaceholder')}
              />
              <Textarea
                label={t('sessions.live.outcomeSummary')}
                value={sessionData.outcomeSummary}
                onChange={(e) => {
                  hasUserInputRef.current = true
                  setSessionData({ ...sessionData, outcomeSummary: e.target.value })
                  // Trigger immediate autosave on input change
                  if (draftSaveTimeoutRef.current) {
                    clearTimeout(draftSaveTimeoutRef.current)
                  }
                }}
                placeholder={t('sessions.live.outcomeSummaryPlaceholder')}
                rows={4}
              />
            </div>
          </Card>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {t('sessions.live.duration')}: {formatTime(sessionDuration)} • {t('sessions.live.vitals')}: {vitalSigns.length} • {t('sessions.live.notesCount')}: {quickNotes.length}
          </div>
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            <Button variant="secondary" onClick={onClose} className="flex-1 sm:flex-initial text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3">
              {t('sessions.live.cancel')}
            </Button>
            <Button 
              onClick={handleSaveSession} 
              disabled={saving || (!initialClientId && !selectedClientId)}
              className="flex-1 sm:flex-initial text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3"
            >
              {saving ? t('sessions.live.saving') : <><Save size={16} className="sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />{t('sessions.live.saveSession')}</>}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Draft Manager Modal */}
      <AnimatePresence>
        {showDraftManager && organizationId && userId && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDraftManager(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{t('sessions.live.savedDrafts')}</h3>
                  <button
                    onClick={() => setShowDraftManager(false)}
                    className="p-1.5 sm:p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <X size={18} className="sm:w-5 sm:h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  <DraftManager
                    organizationId={organizationId}
                    userId={userId}
                    formType="session"
                    onLoadDraft={handleLoadDraft}
                    onClose={() => setShowDraftManager(false)}
                  />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LiveSessionRecorder

